import React, { useCallback, useContext, useRef, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import NotesIcon from '@mui/icons-material/Notes'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import moment from 'moment'
import { useControlRoomLogs } from './useControlRoomLogs'
import {
  AssignmentInd,
  AssignmentLate,
  AssignmentReturn,
  TextDecrease,
  TextIncrease,
} from '@mui/icons-material'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import { ControlRoomContext } from './ControlPage'
import { playIncidentLogPing, playLogPing } from './chatPing'

const logTypeStyles = {
  Incident: { bg: '#FCE7F3', color: '#85276A' },
  System: { bg: '#E2E8F0', color: '#242F40' },
  Command: { bg: '#FEE2E2', color: '#E32525' },
  Decision: { bg: '#DBEAFE', color: '#1F3D7C' },
  Log: { bg: '#DCFCE7', color: '#00964C' },
}

const controlLogPageSize = 10

const LogRow = ({ log: passedLog, controlRoomID, fontSize }) => {
  const [log, setLog] = useState(passedLog)
  const [openTacConfirmation, setOpenTacConfirmation] = useState(false)
  const style = logTypeStyles[log.type] || {
    bg: '#E6F7F9',
    color: '#1E7F88',
  }
  const { setToast } = useContext(GlobalContext)
  const { activeIncidents = [], setSelectedIncident } =
    useContext(ControlRoomContext)

  const openLinkedIncident = () => {
    if (log.type !== 'Incident' || !log.linkedIncident) return

    const linkedIncidentID = String(log.linkedIncident)
    const activeIncident = activeIncidents.find(
      (incident) => String(incident.id || incident._id) === linkedIncidentID,
    )

    setSelectedIncident(
      activeIncident || {
        id: linkedIncidentID,
        _id: linkedIncidentID,
        incidentNumber: log.incidentNumber,
      },
    )
  }

  const handleAddToTacticalCommand = () => {
    if (!openTacConfirmation) {
      setOpenTacConfirmation(true)
      return
    }
    let logToPass = { ...log, controlRoomID }
    delete logToPass.author
    console.log('logToPass', logToPass)
    API.post('/commandcontrol/logs/addnotetotacticalcommand', logToPass).then(
      (res) => {
        if (res.status === 200) {
          setOpenTacConfirmation(false)
          setToast({ message: 'Log Added to Tactical Log' })
          setLog((prev) => ({ ...prev, isAddedToTactical: true }))
        }
      },
    )
  }

  return (
    <Box
      sx={{
        py: 1.25,
        px: 1,
        borderRadius: 2,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: '#F8FAFC',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Tooltip title={moment(log.createdAt).format('Do MMM YYYY HH:mm:ss')}>
          <Typography
            variant="caption"
            sx={{
              minWidth: 76,
              color: '#64748B',
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              fontSize,
            }}
          >
            {moment(log.createdAt).format('HH:mm:ss')}
          </Typography>
        </Tooltip>

        {log.type && (
          <Box
            onClick={openLinkedIncident}
            sx={{
              backgroundColor: style.bg,
              color: style.color,
              fontWeight: 900,
              fontSize,
              padding: 1,
              borderRadius: 1,
              minWidth: 92,
              textAlign: 'center',
              cursor:
                log.type === 'Incident' && log.linkedIncident
                  ? 'pointer'
                  : 'default',
              '&:hover':
                log.type === 'Incident' && log.linkedIncident
                  ? {
                      filter: 'brightness(0.96)',
                      textDecoration: 'underline',
                    }
                  : {},
            }}
          >
            {log.type}
          </Box>
        )}

        <Typography
          variant="body2"
          sx={{
            flex: 1,
            color: '#334155',
            lineHeight: 1.55,
            fontSize,
          }}
        >
          {log.information}
        </Typography>

        <Stack direction="row" gap={0.5} alignItems="center">
          {!['Incident', 'System'].includes(log?.type) && (
            <Tooltip
              title={
                log?.isAddedToTactical
                  ? 'Added to Tactical Command Log'
                  : 'Add to Tactical Command Log'
              }
            >
              <IconButton
                onClick={() =>
                  !log?.isAddedToTactical && handleAddToTacticalCommand()
                }
                disabled={['Incident'].includes(log?.type)}
              >
                <AssignmentLate
                  color={log?.isAddedToTactical ? 'success' : 'inherit'}
                  fontSize={String(fontSize)}
                />
              </IconButton>
            </Tooltip>
          )}
          {log.author && (
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontSize={fontSize}
              >
                {log.author.firstName} {log.author.lastName}
              </Typography>

              <Avatar
                src={log.author?.photograph}
                sx={{
                  width: fontSize + 14,
                  height: fontSize + 14,
                  borderRadius: 2,
                  border: '2px solid #E2E8F0',
                }}
              >
                {log.author.firstName?.[0]}
                {log.author.lastName?.[0]}
              </Avatar>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Dialog
        open={openTacConfirmation}
        onClose={() => setOpenTacConfirmation(false)}
      >
        <DialogTitle>Add to Tactical Log</DialogTitle>
        <DialogContent>
          Are you sure you want to add this log to the global Tactical Log?
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setOpenTacConfirmation(false)}
          >
            Cancel
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => handleAddToTacticalCommand()}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Module-level so a log never chimes twice even when the panel remounts or
// another instance polls the same feed
const chimedLogIds = new Set()

// Log types that get the single ding; every other type gets the double
const singleToneLogTypes = new Set(['Incident', 'System'])

// A resource becoming available again already plays its own tone (the
// stand-down acknowledgement chime on the control page), so the entries
// written alongside it stay silent here. Wording is the server's — see
// resourceActions.js status-change logs and the mobile stand-down ack.
const isResourceAvailableLog = (log) =>
  /changed status from .+ to Available$/.test(log.information || '') ||
  /acknowledged the stand down/i.test(log.information || '')

const CommandLogs = ({
  controlRoomID,
  compact = false,
  maxHeight,
  paperSx = {},
}) => {
  const { user } = useContext(UserContext)
  const userIdRef = useRef(user?._id)
  userIdRef.current = user?._id

  // Chime for entries other people (or the system/crews) added: a single
  // ding for incident-timeline and system entries, a rising double ding for
  // everything else (command, decision, manual logs). Incident creation
  // itself writes no log entry, so this never doubles up with the
  // new-incident alert sound.
  const handleNewLogs = useCallback((freshLogs) => {
    const notable = freshLogs.filter((log) => {
      if (chimedLogIds.has(log._id)) return false
      if (isResourceAvailableLog(log)) return false
      const authorId =
        typeof log.author === 'object' ? log.author?._id : log.author
      return !authorId || String(authorId) !== String(userIdRef.current)
    })
    if (!notable.length) return
    notable.forEach((log) => chimedLogIds.add(log._id))
    // A mixed batch plays the double ding only — the two on top of each
    // other would just be noise
    if (notable.some((log) => !singleToneLogTypes.has(log.type))) playLogPing()
    else playIncidentLogPing()
  }, [])

  const { logs, loading, loadOlder, loadingOlder } = useControlRoomLogs(
    controlRoomID,
    2000,
    controlLogPageSize,
    { onNewLogs: handleNewLogs },
  )

  const [logFilter, setLogFilter] = useState([
    'Incident',
    'System',
    'Command',
    'Decision',
    'Log',
  ])

  const [fontSize, setFontSize] = useState(12)

  const logTypes = ['Incident', 'System', 'Command', 'Decision', 'Log']

  const filteredLogs = logs.filter((x) => logFilter.includes(x.type))

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(255,255,255,0.96)',
        boxShadow: '0 16px 42px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight,
        height: '100%',
        ...paperSx,
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        spacing={2}
        sx={{
          p: 2,
          flexShrink: 0,
          background:
            'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #239AA4 170%)',
          color: 'white',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.12)',
            }}
          >
            <NotesIcon />
          </Box>

          <Stack spacing={0}>
            <Typography fontWeight={900} color="white">
              Control Logs
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              {filteredLogs.length} visible of {logs.length} loaded
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" gap={1} flexWrap="wrap">
          <TextDecrease
            color="white"
            onClick={() => setFontSize((prev) => prev - 1)}
            sx={{ cursor: 'pointer' }}
          />
          <TextIncrease
            color="white"
            onClick={() => setFontSize((prev) => prev + 1)}
            sx={{ cursor: 'pointer' }}
          />
          {logTypes.map((type) => {
            const style = logTypeStyles[type]
            const active = logFilter.includes(type)

            return (
              <Chip
                key={type}
                label={type}
                size="small"
                onClick={() =>
                  setLogFilter((prev) =>
                    prev.includes(type)
                      ? prev.filter((item) => item !== type)
                      : [...prev, type],
                  )
                }
                sx={{
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontWeight: 900,
                  backgroundColor: active ? style.bg : 'rgba(255,255,255,0.08)',
                  color: active ? style.color : 'rgba(255,255,255,0.78)',
                  border: active
                    ? '1px solid transparent'
                    : '1px solid rgba(255,255,255,0.18)',
                  '&:hover': {
                    backgroundColor: active
                      ? style.bg
                      : 'rgba(255,255,255,0.14)',
                  },
                }}
              />
            )
          })}
        </Stack>
      </Stack>

      <Box
        sx={{
          p: 2,
          minHeight: 0,
          ...(maxHeight
            ? {
                overflow: 'auto',
                flex: 1,
              }
            : {}),
          ...(compact
            ? {
                maxHeight: maxHeight ? undefined : { xs: 380, lg: 430 },
                overflow: 'auto',
              }
            : {}),
        }}
      >
        {loading && !logs.length ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#239AA4' }} />
            <Typography variant="body2" color="text.secondary">
              Loading control logs…
            </Typography>
          </Stack>
        ) : logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No logs yet.
          </Typography>
        ) : filteredLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No logs match the selected filters.
          </Typography>
        ) : (
          <Stack divider={<Divider />}>
            {filteredLogs.map((log) => (
              <LogRow
                key={log._id}
                log={log}
                controlRoomID={controlRoomID}
                fontSize={fontSize}
              />
            ))}
          </Stack>
        )}

        {logs.length > 0 && (
          <Button
            fullWidth
            onClick={loadOlder}
            disabled={loadingOlder}
            endIcon={
              loadingOlder ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ExpandMoreIcon />
              )
            }
            sx={{
              mt: 2,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              color: '#239AA4',
              backgroundColor: '#E6F7F9',
              '&:hover': {
                backgroundColor: '#D7F1F4',
              },
            }}
          >
            {loadingOlder ? 'Loading older logs…' : 'Load older logs'}
          </Button>
        )}
      </Box>
    </Paper>
  )
}

export default CommandLogs
